import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation } from '@apollo/client';
import { memberfulQueries } from '../../../graphql/queries';
import mutations from '../../../graphql/mutations.js';

const formatPrice = (cents) => {
  if (!cents && cents !== 0) return '';
  return '$' + (cents / 100).toFixed(2);
};

const buildPlanOptions = (subscriptions) => {
  if (!subscriptions) return [];
  const options = [];
  for (const sub of subscriptions) {
    if (!sub.subscriptionPlans) continue;
    const authorName = sub.author ? sub.author.fullName : '';
    for (const plan of sub.subscriptionPlans) {
      options.push({
        id: plan.id,
        label: authorName + ' - ' + sub.title + ' (' + plan.type + ')',
        subscriptionPlanId: plan.id
      });
    }
  }
  return options;
};

const MemberfulPlanMapping = () => {
  const [selectedMappings, setSelectedMappings] = useState({});
  const [savingPlan, setSavingPlan] = useState(null);

  const { data: plansData, loading: plansLoading } = useQuery(memberfulQueries.MemberfulPlans, {
    fetchPolicy: 'no-cache'
  });

  const { data: mappingsData, loading: mappingsLoading, refetch: refetchMappings } = useQuery(
    memberfulQueries.MemberfulPlanMappings,
    { fetchPolicy: 'no-cache' }
  );

  const { data: subsData } = useQuery(memberfulQueries.SubscriptionsAdminWithPlans, {
    fetchPolicy: 'no-cache'
  });

  const [saveMapping] = useMutation(mutations.SaveMemberfulPlanMapping);
  const [deleteMapping] = useMutation(mutations.DeleteMemberfulPlanMapping);

  const plans = plansData ? plansData.memberfulPlans : [];
  const storedMappings = mappingsData ? mappingsData.memberfulPlanMappings : [];
  const planOptions = buildPlanOptions(subsData ? subsData.subscriptionsAdminWithPlans : []);

  // index stored mappings by memberful plan id
  const mappingByPlanId = {};
  for (const m of storedMappings) {
    mappingByPlanId[String(m.memberfulPlanId)] = m;
  }

  const getSelectedValue = (planId) => {
    if (selectedMappings[planId] !== undefined) {
      return selectedMappings[planId];
    }
    const stored = mappingByPlanId[String(planId)];
    return stored ? stored.bbSubscriptionPlanId : '';
  };

  const getMappedLabel = (planId) => {
    const stored = mappingByPlanId[String(planId)];
    if (!stored || !stored.subscriptionPlan) return null;
    const sp = stored.subscriptionPlan;
    const sub = sp.subscription;
    const authorName = sub && sub.author ? sub.author.fullName : '';
    return authorName + ' - ' + (sub ? sub.title : '') + ' (' + sp.type + ')';
  };

  const handleSave = async (plan) => {
    const bbSpId = getSelectedValue(plan.id);
    if (!bbSpId) return;
    setSavingPlan(plan.id);
    try {
      await saveMapping({
        variables: {
          memberfulPlanId: String(plan.id),
          memberfulPlanName: plan.name,
          bbSubscriptionPlanId: bbSpId
        }
      });
      // clear local selection since it's now persisted
      setSelectedMappings(prev => {
        const next = { ...prev };
        delete next[plan.id];
        return next;
      });
      await refetchMappings();
    } catch (err) {
      console.error('Save mapping error:', err);
    }
    setSavingPlan(null);
  };

  const handleDelete = async (plan) => {
    setSavingPlan(plan.id);
    try {
      await deleteMapping({
        variables: { memberfulPlanId: String(plan.id) }
      });
      setSelectedMappings(prev => {
        const next = { ...prev };
        delete next[plan.id];
        return next;
      });
      await refetchMappings();
    } catch (err) {
      console.error('Delete mapping error:', err);
    }
    setSavingPlan(null);
  };

  if (plansLoading || mappingsLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant='body2' sx={{ mt: 1 }}>Loading plan data...</Typography>
      </Box>
    );
  }

  return (
    <div>
      <Typography variant='body2' sx={{ mb: 2 }}>
        Map each Memberful plan to a BridgeBee subscription plan. These mappings are used during migration and when filtering members by plan.
      </Typography>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Memberful Plan</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>BB Subscription Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => {
              const isMapped = !!mappingByPlanId[String(plan.id)];
              const currentValue = getSelectedValue(plan.id);
              const hasLocalChange = selectedMappings[plan.id] !== undefined;
              const isSaving = savingPlan === plan.id;

              return (
                <TableRow key={plan.id}>
                  <TableCell>
                    <Typography variant='body2'>{plan.name}</Typography>
                    <Typography variant='caption' color='text.secondary'>ID: {plan.id}</Typography>
                  </TableCell>
                  <TableCell>{formatPrice(plan.priceCents)}</TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    <FormControl fullWidth size='small'>
                      <Select
                        value={currentValue}
                        displayEmpty
                        onChange={(e) => setSelectedMappings(prev => ({
                          ...prev,
                          [plan.id]: e.target.value
                        }))}
                      >
                        <MenuItem value=''>
                          <em>-- Select BB Plan --</em>
                        </MenuItem>
                        {planOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.subscriptionPlanId}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {isMapped && !hasLocalChange && (
                      <Typography variant='caption' color='text.secondary'>
                        {getMappedLabel(plan.id)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isMapped
                      ? <Chip label='Mapped' color='success' size='small' />
                      : <Chip label='Unmapped' color='default' size='small' />}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant='contained'
                        size='small'
                        disabled={!currentValue || isSaving}
                        onClick={() => handleSave(plan)}
                      >
                        {isSaving ? <CircularProgress size={20} /> : 'Save'}
                      </Button>
                      {isMapped && (
                        <IconButton
                          size='small'
                          color='error'
                          disabled={isSaving}
                          onClick={() => handleDelete(plan)}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {plans.length === 0 && (
        <Typography variant='body2' sx={{ mt: 2 }}>No plans found from Memberful.</Typography>
      )}
    </div>
  );
};

export default MemberfulPlanMapping;
